import { useMemo } from 'react';
import { Permission } from '@insio/shared-types';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { usePermissions } from '@/features/auth';
import { ASSIGNABLE_ROLES } from '../constants/roles';

/**
 * Returns the assignable account roles as Select options with label and description, for a caller
 * holding USERS_MANAGE.
 *
 * A master account's people and their roles live on the master account, never on an account membership,
 * so no master account role is among them.
 *
 * Extracted to avoid duplicating the mapping in every modal that needs role options.
 */
export function useRoleOptions() {
  const tRole = useEnumTranslation('userRole');
  const tDescription = useEnumTranslation('userRoleDescription');
  const { hasPermission } = usePermissions();
  const canManageAccountRoles = hasPermission(Permission.USERS_MANAGE);

  const options = useMemo(
    () =>
      canManageAccountRoles
        ? ASSIGNABLE_ROLES.map(role => ({
            value: role,
            label: tRole(role),
            description: tDescription(role)
          }))
        : [],
    [tRole, tDescription, canManageAccountRoles]
  );

  return { options };
}
